import React, { useEffect, useState, useCallback, useRef } from 'react';
import { host, formatDateTime } from '../script/variables';
import Spinner from 'react-bootstrap/esm/Spinner';
import InfiniteScroll from 'react-infinite-scroll-component';
import OrderPaymentStatusPartner from './OrderPaymentStatusPartner';
import InvoicePage from '../InvoicePage';
import TakeOrder from './TakeOrder'; // adjust path to wherever TakeOrder.jsx lives
import '../style/orders.css';

const TAX_RATE      = 0.05;
const POLL_INTERVAL = 3000;
const ALERT_INTERVAL     = 3000;   // how often the repeating alarm re-fires
const SOUND_MUTE_LS_KEY  = 'partnerOrderSoundMuted';

// Online = everything that is NOT completed or cancelled
const ONLINE_STATUSES    = ['Pending', 'Accepted', 'Preparing', 'Ready', 'Hold'];
const PENDING_STATUSES   = ['Pending'];
const ACTIVE_STATUSES    = ['Accepted', 'Preparing', 'Ready', 'Hold'];
const COMPLETED_STATUSES = ['Completed', 'Due'];
const CANCELLED_STATUSES = ['Cancelled','Rejected'];

const STATUS_META = {
  Pending:   { color: '#f59e0b', bg: '#fef3c7', icon: '⏳' },
  Accepted:  { color: '#10b981', bg: '#d1fae5', icon: '✓'  },
  Rejected:  { color: '#ef4444', bg: '#fee2e2', icon: '✕'  },
  Preparing: { color: '#3b82f6', bg: '#dbeafe', icon: '👨‍🍳' },
  Ready:     { color: '#10b981', bg: '#d1fae5', icon: '✅' },
  Hold:      { color: '#8b5cf6', bg: '#ede9fe', icon: '⏸'  },
  Cancelled: { color: '#ef4444', bg: '#fee2e2', icon: '✕'  },
  Completed: { color: '#6366f1', bg: '#ede9fe', icon: '💜' },
  Due:       { color: '#f97316', bg: '#ffedd5', icon: '⏸'  },
};

// ── Audio ─────────────────────────────────────────────────────────────────────
// TODO: confirm this matches the actual filename/path under /public.
const NEW_ORDER_SOUND_SRC = '/order_alert.wav';

// Reused across calls instead of a fresh `new Audio()` each time — cheaper,
// and lets overlapping calls (e.g. the repeating alarm firing while a manual
// retrigger happens) just restart the same element rather than stacking audio.
let _orderAudioEl = null;
const getOrderAudioEl = () => {
  if (!_orderAudioEl) {
    _orderAudioEl = new Audio(NEW_ORDER_SOUND_SRC);
    _orderAudioEl.preload = 'auto';
  }
  return _orderAudioEl;
};

const playNewOrderSound = async () => {
  try {
    const el = getOrderAudioEl();
    el.currentTime = 0;
    await el.play();
  } catch (e) {
    // Autoplay can be blocked until the owner interacts with the page at least
    // once — this is expected on first load, not a bug.
    console.warn('playNewOrderSound:', e);
  }
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const safeStr      = (v, fb = '--') => (v === null || v === undefined || v === '' ? fb : String(v));
const safeNum      = (v, fb = 0)   => ((typeof v === 'number' || typeof v === 'string') && !isNaN(Number(v)) ? Number(v) : fb);
const calcSubtotal = (items = [])  => items.reduce((s, i) => s + safeNum(i.price) * safeNum(i.quantity, 1), 0);
const getToken     = ()            => localStorage.getItem('partnertoken');
const getDisplayItems = (order) => {
  const items = Array.isArray(order.items)
    ? order.items.map((i, idx) => ({
        key: i._id || `${i.item}-${idx}`,
        name: safeStr(i.item),
        quantity: safeNum(i.quantity, 1),
        price: safeNum(i.price),
        hasPrice: true,
      }))
    : [];

  const comboItems = [];
  (order.combos || []).forEach((combo) => {
    const comboQty = safeNum(combo.quantity, 1);
    (combo.dishes || []).forEach((d, di) => {
      comboItems.push({
        key: `${combo.comboId || combo.name}-${di}`,
        name: safeStr(d.name),
        quantity: safeNum(d.quantity, 1) * comboQty,
        price: null,
        hasPrice: false,
      });
    });
  });

  return [...items, ...comboItems];
};

const istToday = () => {
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  return new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);
};

const istStartOfDay = (dateStr) =>
  new Date(`${dateStr}T00:00:00.000+05:30`).toISOString();

// ── HashMap helpers ───────────────────────────────────────────────────────────
const toMap = (arr) => {
  const m = new Map();
  for (const o of arr) { if (o?.orderId != null) m.set(String(o.orderId), o); }
  return m;
};
const toArray = (m) => Array.from(m.values());

const mergeIntoMap = (existingMap, incoming) => {
  const nextMap  = new Map(existingMap);
  const trulyNew = [];
  for (const o of incoming) {
    if (o?.orderId == null) continue;
    const id = String(o.orderId);
    if (nextMap.has(id)) { nextMap.set(id, { ...nextMap.get(id), ...o }); }
    else { trulyNew.push(o); nextMap.set(id, o); }
  }
  return { nextMap, trulyNew };
};

const prependToMap = (existingMap, newOrders) => {
  const m = new Map();
  for (const o of newOrders) { if (o?.orderId != null) m.set(String(o.orderId), o); }
  for (const [k, v] of existingMap) { if (!m.has(k)) m.set(k, v); }
  return m;
};

// ── MenuPickerModal ───────────────────────────────────────────────────────────
const MenuPickerModal = ({ cafeid, onAdd, onClose }) => {
  const [menu, setMenu]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [mealType, setMealType] = useState(null);
  const [category, setCategory] = useState(null);
  const [search, setSearch]     = useState('');
  const [size, setSize]         = useState('medium');
  const [qty, setQty]           = useState(1);
  const [picked, setPicked]     = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${host}/order/fetchMenu?cafeid=${cafeid}`, {
          headers: { Authorization: getToken() },
        });
        if (res.ok) {
          const data = await res.json();
          const arr  = Array.isArray(data?.menu) ? data.menu : [];
          setMenu(arr);
          if (arr.length) setMealType(arr[0]);
        }
      } catch (e) { console.error('fetchMenu:', e); }
      finally { setLoading(false); }
    })();
  }, [cafeid]);

  useEffect(() => {
    setCategory(mealType?.categories?.length ? mealType.categories[0] : null);
    setPicked(null);
  }, [mealType]);

  const dishes   = category?.dishes || [];
  const filtered = search
    ? dishes.filter(d => d.name.toLowerCase().includes(search.toLowerCase()))
    : dishes;

  const getPrice = (dish, sz) => {
    const obj = dish?.[sz];
    if (!obj) return 0;
    const b = safeNum(obj.price);
    return Math.round(b - b * safeNum(obj.discount) / 100);
  };

  const handleAdd = () => {
    if (!picked) return;
    onAdd({
      item: picked.name, quantity: qty,
      price: getPrice(picked, size), discount: 0, size,
      _id: `new-${Date.now()}`,
    });
    onClose();
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="menu-picker-modal" onClick={e => e.stopPropagation()}>
        <div className="menu-picker-header">
          <h5>Add Item to Order</h5>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        {loading ? (
          <div className="menu-picker-loading"><Spinner animation="border" size="sm" /></div>
        ) : (
          <div className="menu-picker-body">
            <div className="menu-tabs">
              {menu.map(mt => (
                <button key={mt.type} className={`menu-tab${mealType?.type === mt.type ? ' active' : ''}`}
                  onClick={() => setMealType(mt)}>{mt.type}</button>
              ))}
            </div>
            {mealType?.categories?.length > 0 && (
              <div className="menu-cat-row">
                {mealType.categories.map(cat => (
                  <button key={cat.name}
                    className={`menu-cat-chip${category?.name === cat.name ? ' active' : ''}`}
                    onClick={() => { setCategory(cat); setPicked(null); }}>{cat.name}</button>
                ))}
              </div>
            )}
            <input className="menu-search" type="text" placeholder="Search dishes..."
              value={search} onChange={e => setSearch(e.target.value)} />
            <div className="menu-dish-list">
              {filtered.length === 0 && <p className="menu-empty">No dishes found</p>}
              {filtered.map(dish => (
                <div key={dish.name}
                  className={`menu-dish-row${picked?.name === dish.name ? ' picked' : ''}`}
                  onClick={() => { setPicked(dish); setSize('medium'); setQty(1); }}>
                  <span className={`veg-dot ${dish.type}`} />
                  <span className="menu-dish-name">{dish.name}</span>
                  <span className="menu-dish-price">₹{getPrice(dish, 'medium')}</span>
                </div>
              ))}
            </div>
            {picked && (
              <div className="menu-picked-controls">
                <div className="menu-size-row">
                  {['small', 'medium', 'large'].map(sz => (
                    <button key={sz} className={`size-btn${size === sz ? ' active' : ''}`}
                      onClick={() => setSize(sz)}>{sz} · ₹{getPrice(picked, sz)}</button>
                  ))}
                </div>
                <div className="menu-qty-row">
                  <span className="ops-label">Qty</span>
                  <div className="qty-stepper">
                    <button onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
                    <span>{qty}</span>
                    <button onClick={() => setQty(q => q + 1)}>+</button>
                  </div>
                </div>
                <div className="menu-picked-summary">
                  <span>{picked.name} × {qty}</span>
                  <span>₹{getPrice(picked, size) * qty}</span>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="modal-actions">
          <button className="btn-confirm" onClick={handleAdd} disabled={!picked}>✓ Add to Order</button>
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── EditOrderModal ────────────────────────────────────────────────────────────
const EditOrderModal = ({ order, cafeid, onClose, onSaved }) => {
  const [items, setItems]         = useState((order.items || []).map(i => ({ ...i, quantity: safeNum(i.quantity, 1) })));
  const [saving, setSaving]       = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [error, setError]         = useState('');

  const subtotal = calcSubtotal(items);
  const tax      = Math.round(subtotal * TAX_RATE);
  const total    = subtotal + tax;

  const updateQty = (idx, d) => setItems(prev => {
    const n = [...prev]; const q = safeNum(n[idx].quantity, 1) + d;
    if (q < 1) return prev;
    n[idx] = { ...n[idx], quantity: q }; return n;
  });

  const removeItem = (idx) => {
    if (items.length === 1) { setError('Order must have at least one item.'); return; }
    setItems(p => p.filter((_, i) => i !== idx)); setError('');
  };

  const addItem = (ni) => setItems(prev => {
    const idx = prev.findIndex(i => i.item === ni.item && (i.size || 'medium') === (ni.size || 'medium'));
    if (idx !== -1) {
      const n = [...prev]; n[idx] = { ...n[idx], quantity: n[idx].quantity + ni.quantity }; return n;
    }
    return [...prev, ni];
  });

  const handleSave = async () => {
    if (!items.length) { setError('Order must have at least one item.'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${host}/partner/updateOrderItems`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({
          orderid: order.orderId,
          items: items.map(i => ({ item: i.item, quantity: i.quantity, price: i.price, discount: safeNum(i.discount), size: i.size })),
          totalPrice: total,
        }),
      });
      if (res.ok) { onSaved(); onClose(); }
      else { const b = await res.json().catch(() => ({})); setError(b?.message || 'Failed to update order.'); }
    } catch { setError('Network error. Please check your connection.'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}>
        <div className="edit-order-modal" onClick={e => e.stopPropagation()}>
          <div className="edit-modal-header">
            <div>
              <h5>Edit Order</h5>
              <span className="order-id-chip">#{safeNum(order.dailyOrderNumber)} · {order.orderId}</span>
            </div>
            <button className="modal-close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="edit-modal-body">
            <div className="edit-section">
              <div className="edit-section-titlerow">
                <p className="edit-section-title">Items ({items.length})</p>
                <button className="btn-add-item" onClick={() => setShowPicker(true)}>+ Add Item</button>
              </div>
              <div className="edit-items-list">
                {items.map((item, idx) => (
                  <div className="edit-item-row" key={item._id || idx}>
                    <div className="edit-item-info">
                      <span className="edit-item-name">{safeStr(item.item)}</span>
                      {item.size && <span className="edit-item-size">{item.size}</span>}
                      <span className="edit-item-unitprice">₹{safeNum(item.price)} ea.</span>
                    </div>
                    <div className="edit-item-controls">
                      <div className="qty-stepper">
                        <button onClick={() => updateQty(idx, -1)}>−</button>
                        <span>{item.quantity}</span>
                        <button onClick={() => updateQty(idx, 1)}>+</button>
                      </div>
                      <span className="edit-item-linetotal">₹{safeNum(item.price) * item.quantity}</span>
                      <button className="btn-remove-item" onClick={() => removeItem(idx)}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="edit-totals">
              <div className="totals-row"><span>Subtotal</span><span>₹{subtotal}</span></div>
              <div className="totals-row"><span>Tax ({(TAX_RATE * 100).toFixed(0)}%)</span><span>₹{tax}</span></div>
              <div className="totals-row total-final"><span>Total</span><span>₹{total}</span></div>
            </div>
            {error && <p className="edit-error">{error}</p>}
          </div>
          <div className="modal-actions">
            <button className="btn-confirm" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : '✓ Save Changes'}
            </button>
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
      {showPicker && (
        <MenuPickerModal cafeid={cafeid} onAdd={addItem} onClose={() => setShowPicker(false)} />
      )}
    </>
  );
};

// ── PaymentModal ──────────────────────────────────────────────────────────────
const PaymentModal = ({ order, onClose, onPaid }) => {
  const total = safeNum(order.totalPrice);
  const [cashAmount, setCashAmount]       = useState(0);
  const [digitalAmount, setDigitalAmount] = useState(0);
  const [digitalMethod, setDigitalMethod] = useState('UPI');
  const [isDue, setIsDue]                 = useState(false);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');

  const handleSubmit = async () => {
    setError('');
    if (cashAmount < 0 || digitalAmount < 0) { setError('Amounts cannot be negative.'); return; }
    if (!isDue && cashAmount + digitalAmount !== total) {
      setError(`Cash + Digital (₹${cashAmount + digitalAmount}) must equal ₹${total}.`); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${host}/order/updateStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({
          orderid: order.orderId, status: isDue ? 'Due' : 'Completed',
          totalAmount: total, cashAmount: safeNum(cashAmount),
          digitalAmount: safeNum(digitalAmount),
          digitalMethod: isDue ? 'Due' : safeStr(digitalMethod, 'upi'),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) { onPaid(); onClose(); }
      else { setError(body?.message || 'Payment failed. Order may already be completed.'); }
    } catch { setError('Network error. Please try again.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop">
      <div className="payment-modal">
        <div className="edit-modal-header">
          <h5>Collect Payment</h5>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-total">
          <span className="modal-total-label">Total Due</span>
          <span className="modal-total-value">₹{total}</span>
        </div>
        <div className="quick-buttons">
          <button onClick={() => { setCashAmount(total); setDigitalAmount(0); setIsDue(false); }}>💵 Full Cash</button>
          <button onClick={() => { setCashAmount(0); setDigitalAmount(total); setIsDue(false); }}>📱 Full Digital</button>
          <button onClick={() => { setCashAmount(0); setDigitalAmount(0); setIsDue(true); }}>⏸ Due</button>
        </div>
        {!isDue && (
          <>
            <div className="modal-split-labels"><span>💵 Cash</span><span>📱 Digital</span></div>
            <div className="modal-split-row">
              <input type="number" value={cashAmount} min={0}
                onChange={e => { const v = Math.max(0, safeNum(Number(e.target.value))); setCashAmount(v); setDigitalAmount(Math.max(0, total - v)); }} />
              <input type="number" value={digitalAmount} min={0}
                onChange={e => { const v = Math.max(0, safeNum(Number(e.target.value))); setDigitalAmount(v); setCashAmount(Math.max(0, total - v)); }} />
            </div>
            <select value={digitalMethod} onChange={e => setDigitalMethod(e.target.value)}>
              <option value="UPI">UPI</option>
              <option value="Merchant">Merchant</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Debit Card">Debit Card</option>
            </select>
          </>
        )}
        {isDue && <div className="due-notice">⚠️ This order will be marked as Due. Collect payment later.</div>}
        {error && <p className="edit-error" style={{ marginTop: '0.5rem' }}>{error}</p>}
        <div className="modal-actions" style={{ marginTop: '1rem' }}>
          <button className="btn-confirm" onClick={handleSubmit} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : '✓ Confirm Payment'}
          </button>
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── SendBillModal ─────────────────────────────────────────────────────────────
const SendBillModal = ({ order, onClose }) => {
  const [phone, setPhone]     = useState(order.customerPhone || '');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState('');

  const sendBill = async () => {
    if (!phone || phone.length < 10) { setError('Enter valid phone number'); return; }
    setSending(true); setError('');
    try {
      const res = await fetch(`${host}/partner/sendbill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({ orderid: order.orderId, cafeid: order.cafeid, phone }),
      });
      const data = await res.json();
      if (res.ok) { setSuccess(true); setTimeout(() => onClose(), 1200); }
      else { setError(data?.message || 'Failed to send bill'); }
    } catch { setError('Network error'); }
    setSending(false);
  };

  return (
    <div className="modal-backdrop">
      <div className="payment-modal">
        <div className="edit-modal-header">
          <h5>Send Bill</h5>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        <label>Customer Phone</label>
        <input type="text" value={phone} placeholder="Enter phone number"
          onChange={e => setPhone(e.target.value.replace(/\D/g, ''))} />
        {error && <p className="edit-error">{error}</p>}
        {success && <p style={{ color: '#16a34a', marginTop: '0.5rem' }}>Bill sent successfully</p>}
        <div className="modal-actions">
          <button className="btn-confirm" onClick={sendBill} disabled={sending}>
            {sending ? <Spinner animation="border" size="sm" /> : 'Send Bill'}
          </button>
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── InvoiceModal ──────────────────────────────────────────────────────────────
const InvoiceModal = ({ orderId, onClose }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="modal-backdrop invoice-modal-backdrop" onClick={onClose}>
      <div className="invoice-modal" onClick={e => e.stopPropagation()}>
        <button className="invoice-modal-close" onClick={onClose} aria-label="Close invoice">✕</button>
        <InvoicePage orderNumber={orderId} />
      </div>
    </div>
  );
};

// ── AcceptRejectBar ───────────────────────────────────────────────────────────
const AcceptRejectBar = ({ order, onStatusChanged }) => {
  const [loading, setLoading] = useState(null);
  const [error, setError]     = useState('');

  const act = async (action) => {
    setLoading(action); setError('');
    try {
      const res = await fetch(`${host}/order/updateDeliveryStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({
          orderid: order.orderId,
          status: action === 'accept' ? 'Accepted' : 'Rejected',
          totalAmount: order.totalPrice,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) { onStatusChanged(); }
      else { setError(body?.message || 'Action failed.'); }
    } catch { setError('Network error.'); }
    finally { setLoading(null); }
  };

  return (
    <div className="accept-reject-bar">
      <div className="arb-label"><span className="arb-dot" />New order — awaiting confirmation</div>
      <div className="arb-actions">
        <button className="arb-btn arb-reject" onClick={() => act('reject')} disabled={!!loading}>
          {loading === 'reject' ? <Spinner animation="border" size="sm" /> : '✕ Reject'}
        </button>
        <button className="arb-btn arb-accept" onClick={() => act('accept')} disabled={!!loading}>
          {loading === 'accept' ? <Spinner animation="border" size="sm" /> : '✓ Accept'}
        </button>
      </div>
      {error && <p className="arb-error">{error}</p>}
    </div>
  );
};

// ── OrderCard ─────────────────────────────────────────────────────────────────
const OrderCard = ({
  order, cafeid, socket, changeStatus, setChangeStatus,
  isOnline, onOpenPayment, onOpenEdit, onOpenInvoice, isNew, onOpenSendBill,
  onStatusChanged, isPendingOnly
}) => {
  const [expanded, setExpanded] = useState(false);

  const orderId    = safeStr(order.orderId, `tmp-${order.dailyOrderNumber}`);
  const total      = safeNum(order.totalPrice);
  const payStatus  = safeStr(order.paymentStatus, 'Pending');
  const delStatus  = safeStr(order.deliveryStatus, '');
  const orderType  = safeStr(order.orderType, 'takeaway');
const items      = Array.isArray(order.items) ? order.items : [];
const displayItems = getDisplayItems(order); // ← items + flattened combo dishes, for display only
const subtotal   = calcSubtotal(items);
  const customer   = safeStr(order.customerName, 'Guest');
  const customerPhone = safeStr(order.customerPhone, '');
  order.cafeid = cafeid;

  const showPrintBill = payStatus === 'Completed' && delStatus === 'Completed';

  return (
    <div className={`order-card${expanded ? ' expanded' : ''}${isNew ? ' order-card--new' : ''}${isPendingOnly ? ' order-card--pending' : ''}`}>
      <div className="order-card-header" onClick={() => setExpanded(p => !p)} style={{ cursor: 'pointer' }}>
        <div className="order-header-left">
          <h5 className="order-daily-num">#{safeNum(order.dailyOrderNumber)}</h5>
          <div className="order-header-meta">
            <span className="order-id" title={orderId}>{orderId.slice(-8)}</span>
            <span className="order-type-badge">
              {orderType === 'dine-in'
                ? `🍽️ Table ${safeStr(order.tableNumber)}${order.areaName ? ` · ${order.areaName}` : ''}`
                : `📦 ${orderType.toUpperCase()}`}
            </span>
            {delStatus && STATUS_META[delStatus] && (
              <span className="pay-status-badge"
                style={{ color: STATUS_META[delStatus].color, background: STATUS_META[delStatus].bg }}>
                {STATUS_META[delStatus].icon} {delStatus}
              </span>
            )}
          </div>
        </div>
        <div className="order-header-right">
          {isNew && <span className="new-badge">NEW</span>}
          <span className="order-total-chip">₹{total}</span>
          {payStatus !== 'Pending' && (
            <span className="pay-status-badge"
              style={{ color: STATUS_META[payStatus]?.color || '#6b7280', background: STATUS_META[payStatus]?.bg || '#f3f4f6' }}>
              {STATUS_META[payStatus]?.icon} {payStatus}
            </span>
          )}
          <span className="expand-chevron">{expanded ? '▲' : '▽'}</span>
        </div>
      </div>

      <p className="order-timestamp">🕐 {order.placedAt ? formatDateTime(order.placedAt) : '--'}</p>

      <p className="order-customer">
        👤 {customer}
        {customerPhone && <span className="order-phone"> · 📞 {customerPhone}</span>}
      </p>

      <div className="order-items-summary">
  {displayItems.map((item) => (
    <div key={item.key} className="order-item-pill">
      <span className="item-qty">{item.quantity}×</span>
      <span className="item-name">{item.name}</span>
      {item.hasPrice && (
        <span className="item-price">₹{item.price * item.quantity}</span>
      )}
    </div>
  ))}
</div>

      {expanded && (
        <div className="order-expanded-body">

          {isPendingOnly && (
            <AcceptRejectBar
              order={order}
              onStatusChanged={() => setChangeStatus(p => !p)}
            />
          )}

          {order.specialInstructions && order.specialInstructions !== '' && (
            <div className="special-instructions">
              <span className="si-label">📝 Special Instructions</span>
              <span className="si-text">{order.specialInstructions}</span>
            </div>
          )}

          {!isPendingOnly && orderId !== '--' && !['Completed', 'Cancelled', 'Rejected', 'Due'].includes(delStatus) && (
            <OrderPaymentStatusPartner
              orderid={orderId} cafeid={cafeid}
              setChangeStatus={setChangeStatus} changeStatus={changeStatus} socket={socket}
            />
          )}

          {!isPendingOnly && (
            <div className="order-card-actions">
              {isOnline && (
                <button className="btn-edit-order" onClick={() => onOpenEdit(order)}>✏️ Edit Order</button>
              )}
              {payStatus === 'Pending' && (
                <button className="btn-payment" onClick={() => onOpenPayment(order)}>💳 Collect Payment</button>
              )}
              {showPrintBill && (
                <>
                  <button className="btn-print-bill" onClick={() => onOpenInvoice(order.orderId)}>🧾 Print Bill</button>
                  <button className="btn-print-bill" onClick={() => onOpenSendBill(order)}>📲 Send Bill</button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── NewOrderToast ─────────────────────────────────────────────────────────────
const NewOrderToast = ({ count, onDismiss }) => (
  <div className="new-order-toast" onClick={onDismiss}>
    <span className="toast-bell">🔔</span>
    <span className="toast-text">{count === 1 ? '1 new order!' : `${count} new orders!`}</span>
    <span className="toast-dismiss">✕</span>
  </div>
);

// ── PendingAlertBar ────────────────────────────────────────────────────────────
// Persistent banner shown whenever there are unaccepted orders, regardless of
// which tab is open — this is what the repeating sound alert is tied to.
// Includes the mute/unmute control for that alarm.
const PendingAlertBar = ({ count, muted, onToggleMute }) => (
  <div className={`pending-alert-bar${muted ? ' pending-alert-bar--muted' : ''}`}>
    <span className="pab-text">
      <span className="pab-dot" />
      {count} order{count !== 1 ? 's' : ''} awaiting your confirmation
    </span>
    <button
      type="button"
      className="pab-mute-btn"
      onClick={onToggleMute}
      title={muted ? 'Unmute new-order alert sound' : 'Mute new-order alert sound'}
    >
      {muted ? '🔕 Unmute sound' : '🔔 Mute sound'}
    </button>
  </div>
);


// ── OpenStatusToggle ──────────────────────────────────────────────────────────
// Turns the restaurant's online ordering on/off. Sits beside the sound toggle.
const OpenStatusToggle = ({ isOpen, loading, onToggle }) => (
  <button
    type="button"
    className={`open-status-toggle${isOpen ? ' is-open' : ' is-closed'}`}
    onClick={onToggle}
    disabled={loading}
    title={isOpen ? 'Restaurant is OPEN — click to stop taking orders'
                  : 'Restaurant is CLOSED — click to start taking orders'}
  >
    {loading
      ? <Spinner animation="border" size="sm" />
      : <span className="ost-dot" />}
    <span className="ost-text">{isOpen ? 'Open' : 'Closed'}</span>
    <span className="ost-switch"><span className="ost-knob" /></span>
  </button>
);

// ── TwoColumnOnlinePanel ──────────────────────────────────────────────────────
const TwoColumnOnlinePanel = ({
  orders, cafeid, socket, changeStatus, setChangeStatus,
  isOnline, onOpenPayment, onOpenEdit, onOpenInvoice, onOpenSendBill,
  newOrderIds, onStatusChanged
}) => {
  const pendingOrders = orders.filter(o => safeStr(o.deliveryStatus) === 'Pending');
  const activeOrders  = orders.filter(o => ACTIVE_STATUSES.includes(safeStr(o.deliveryStatus)));

  const cardProps = (order, isPendingOnly) => ({
    key:            safeStr(order.orderId, `tmp-${order.dailyOrderNumber}`),
    order,
    cafeid,
    socket,
    changeStatus,
    setChangeStatus,
    isOnline,
    onOpenPayment,
    onOpenEdit,
    onOpenInvoice,
    onOpenSendBill,
    isNew:          newOrderIds.has(String(order.orderId)),
    onStatusChanged,
    isPendingOnly,
  });

  return (
    <div className="online-two-col">
      <div className="online-col online-col--pending">
        <div className="online-col-header">
          <span className="online-col-dot online-col-dot--pending" />
          <span className="online-col-title">Pending</span>
          <span className="online-col-count">{pendingOrders.length}</span>
        </div>
        {pendingOrders.length === 0 ? (
          <div className="online-col-empty">
            <span className="online-col-empty-icon">🎉</span>
            <p>No pending orders</p>
          </div>
        ) : (
          pendingOrders.map(order => (
            <OrderCard {...cardProps(order, true)} />
          ))
        )}
      </div>

      <div className="online-col online-col--active">
        <div className="online-col-header">
          <span className="online-col-dot online-col-dot--active" />
          <span className="online-col-title">In Progress</span>
          <span className="online-col-count">{activeOrders.length}</span>
        </div>
        {activeOrders.length === 0 ? (
          <div className="online-col-empty">
            <span className="online-col-empty-icon">✅</span>
            <p>No active orders</p>
          </div>
        ) : (
          activeOrders.map(order => (
            <OrderCard {...cardProps(order, false)} />
          ))
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const Orders = ({ cafeid, socket,cafeOpen  }) => {
  const [orders,         setOrders]         = useState([]);
  const [hasMore,        setHasMore]        = useState(true);
  const [activeTab,      setActiveTab]      = useState('online'); // 'online' | 'completed' | 'cancelled'
  const [status,         setStatus]         = useState(ONLINE_STATUSES);
  const [changeStatus,   setChangeStatus]   = useState(false);
  const [date,           setDate]           = useState(istToday());
  const [isDate,         setIsDate]         = useState(true);
  const [orderID,        setOrderId]        = useState('');
  const [onlineCount,    setOnlineCount]    = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [cancelledCount, setCancelledCount] = useState(0);
  const [paymentOrder,   setPaymentOrder]   = useState(null);
  const [editOrder,      setEditOrder]      = useState(null);
  const [invoiceOrderId, setInvoiceOrderId] = useState(null);
  const [newOrderCount,  setNewOrderCount]  = useState(0);
  const [newOrderIds,    setNewOrderIds]    = useState(new Set());
  const [sendBillOrder,  setSendBillOrder]  = useState(null);
    // ── Restaurant open/closed ───────────────────────────────────────────────
  // `cafeOpen` comes from the parent (the cafe object you already load there).
  // Falls back to open so the owner is never silently shut off on a load hiccup.
  const [isOpen, setIsOpen]                     = useState(cafeOpen !== false);
  const [openStatusLoading, setOpenStatusLoading] = useState(false);
  const [openStatusError, setOpenStatusError]     = useState('');


  useEffect(() => {
    if (typeof cafeOpen === 'boolean') setIsOpen(cafeOpen);
  }, [cafeOpen]);

  const toggleOpenStatus = useCallback(async () => {
    const next = !isOpen;
    if (!next && !window.confirm('Close the restaurant? Customers will not be able to place new orders through the QR code, You can still place new orders using the admin panel.')) return;

    setOpenStatusLoading(true);
    setOpenStatusError('');
    try {
      const res = await fetch(`${host}/partner/updateopenstatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({ cafeid, isOpen: next }),   // cafeid in body, not params
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setOpenStatusError(body?.message || 'Could not update status');
        return;
      }
      // Trust the server's value if it echoes one back, else use what we sent.
      const serverVal = body?.isOpen ?? body?.open ?? body?.cafe?.isOpen;
      setIsOpen(typeof serverVal === 'boolean' ? serverVal : next);
    } catch {
      setOpenStatusError('Network error');
    } finally {
      setOpenStatusLoading(false);
    }
  }, [cafeid, isOpen]);


  // ── New-order sound alert ────────────────────────────────────────────────
  // pendingAlertCount = orders still sitting in "Pending" status anywhere for
  // this restaurant (not limited to whatever tab/date filter is on screen).
  // The alarm keeps repeating until this hits 0 — i.e. until every pending
  // order has been accepted or rejected — unless the owner has muted it.
  const [pendingAlertCount, setPendingAlertCount] = useState(0);
  const [soundMuted, setSoundMuted] = useState(() => {
    try { return localStorage.getItem(SOUND_MUTE_LS_KEY) === 'true'; }
    catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(SOUND_MUTE_LS_KEY, String(soundMuted)); } catch { /* ignore */ }
  }, [soundMuted]);

  const isOnlineTab = activeTab === 'online';

  // ── Refs ──────────────────────────────────────────────────────────────────
  const ordersMapRef      = useRef(new Map());
  const latestPlacedAtRef = useRef(null);
  const pollTimerRef      = useRef(null);
  const alertTimerRef     = useRef(null);
  const pnoRef            = useRef(1);
  const pollFnRef         = useRef(null);
  const resetGenRef       = useRef(0);

  const statusRef  = useRef(status);
  const isDateRef  = useRef(isDate);
  const dateRef    = useRef(date);
  const orderIDRef = useRef(orderID);
  const isOnlineTabRef = useRef(isOnlineTab);

  useEffect(() => { statusRef.current      = status;      }, [status]);
  useEffect(() => { isDateRef.current      = isDate;      }, [isDate]);
  useEffect(() => { dateRef.current        = date;        }, [date]);
  useEffect(() => { orderIDRef.current     = orderID;     }, [orderID]);
  useEffect(() => { isOnlineTabRef.current = isOnlineTab; }, [isOnlineTab]);

  const commitMap = useCallback((newMap) => {
    ordersMapRef.current = newMap;
    setOrders(toArray(newMap));
  }, []);

  const advanceCursor = useCallback((list) => {
    let latest = latestPlacedAtRef.current ? new Date(latestPlacedAtRef.current) : null;
    for (const o of list) {
      if (!o.placedAt) continue;
      const t = new Date(o.placedAt);
      if (!latest || t > latest) latest = t;
    }
    if (latest) latestPlacedAtRef.current = latest.toISOString();
  }, []);

  const fetchOrderCount = useCallback(async (statusArr) => {
    try {
      const res = await fetch(`${host}/partner/fetchOrderCount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({ cafeid, status: statusArr, date: dateRef.current || istToday() }),
      });
      return res.ok ? safeNum(await res.json()) : 0;
    } catch { return 0; }
  }, [cafeid]);

  // fetchOrderCount(PENDING_STATUSES) is intentionally NOT date-scoped in
  // practice — an order that came in "today" but crosses midnight while still
  // unaccepted should keep alarming, so we always ask for pending orders on
  // whatever `date` filter is active plus fall back to today's date.
  const updateOrderCounts = useCallback(async () => {
    const [o, c, cn, p] = await Promise.all([
      fetchOrderCount(ONLINE_STATUSES),
      fetchOrderCount(COMPLETED_STATUSES),
      fetchOrderCount(CANCELLED_STATUSES),
      fetchOrderCount(PENDING_STATUSES),
    ]);
    setOnlineCount(o);
    setCompletedCount(c);
    setCancelledCount(cn);
    setPendingAlertCount(p);
  }, [fetchOrderCount]);

  const fetchOrders = useCallback(async (reset = false) => {
    const pn = reset ? 1 : pnoRef.current;
    try {
      const res = await fetch(`${host}/partner/fetchOrders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({
          cafeid, pno: pn,
          status:   statusRef.current,
          isDate:   isDateRef.current,
          fromDate: dateRef.current,
          toDate:   dateRef.current,
          orderID:  orderIDRef.current,
        }),
      });
      if (!res.ok) return;

      const result     = await res.json();
      const data       = Array.isArray(result?.data) ? result.data : [];
      const totalPages = safeNum(result?.pagination?.totalPages, 0);

      setHasMore(pn < totalPages);
      if (data.length > 0) pnoRef.current = pn + 1;

      if (reset) {
        const freshMap = toMap(data);
        commitMap(freshMap);
        setNewOrderIds(new Set());
        if (data.length > 0) { advanceCursor(data); }
        else { latestPlacedAtRef.current = istStartOfDay(dateRef.current || istToday()); }
      } else {
        const { nextMap } = mergeIntoMap(ordersMapRef.current, data);
        commitMap(nextMap);
        advanceCursor(data);
      }
    } catch (e) { console.error('fetchOrders:', e); }
  }, [cafeid, advanceCursor, commitMap]);

  const pollNewOrders = useCallback(async () => {
    if (!isOnlineTabRef.current)      return;
    if (!latestPlacedAtRef.current)   return;
    if (orderIDRef.current)           return;

    const gen = resetGenRef.current;
    try {
      const res = await fetch(`${host}/partner/fetchNewOrders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({
          cafeid,
          status: statusRef.current,
          since:  latestPlacedAtRef.current,
          isDate: isDateRef.current,
          date:   dateRef.current || istToday(),
        }),
      });
      if (!res.ok) return;
      if (resetGenRef.current !== gen) return;

      const result    = await res.json();
      const newOrders = Array.isArray(result?.data) ? result.data : [];
      if (!newOrders.length) return;

      advanceCursor(newOrders);
      const { nextMap, trulyNew } = mergeIntoMap(ordersMapRef.current, newOrders);

      if (trulyNew.length) {
        commitMap(prependToMap(nextMap, trulyNew));
      } else {
        commitMap(nextMap);
      }

      if (trulyNew.length) {
        const freshIds = new Set(trulyNew.map(o => String(o.orderId)));
        setNewOrderIds(ids => { const n = new Set(ids); freshIds.forEach(id => n.add(id)); return n; });
        setTimeout(() => {
          setNewOrderIds(ids => { const n = new Set(ids); freshIds.forEach(id => n.delete(id)); return n; });
        }, 8000);
        setNewOrderCount(c => c + trulyNew.length);
        updateOrderCounts(); // refreshes pendingAlertCount → kicks the repeating alarm on
      }
    } catch (e) { console.error('pollNewOrders:', e); }
  }, [cafeid, advanceCursor, commitMap, updateOrderCounts]);

  useEffect(() => { pollFnRef.current = pollNewOrders; }, [pollNewOrders]);

  // ── Repeating "unaccepted order" alarm ───────────────────────────────────
  // Fires once immediately and then every ALERT_INTERVAL ms for as long as
  // pendingAlertCount > 0. Stops the moment an owner accepts/rejects the
  // last pending order (which drives pendingAlertCount back to 0 via
  // updateOrderCounts), or the moment sound is muted.
  useEffect(() => {
    clearInterval(alertTimerRef.current);
    if (pendingAlertCount > 0 && !soundMuted) {
      playNewOrderSound();
      alertTimerRef.current = setInterval(() => { playNewOrderSound(); }, ALERT_INTERVAL);
    }
    return () => clearInterval(alertTimerRef.current);
  }, [pendingAlertCount, soundMuted]);

  // ── Tab → status mapping ──────────────────────────────────────────────────
  const getStatusForTab = (tab) => {
    if (tab === 'online')    return ONLINE_STATUSES;
    if (tab === 'completed') return COMPLETED_STATUSES;
    return CANCELLED_STATUSES;
  };

  // ── Main reset effect ─────────────────────────────────────────────────────
  useEffect(() => {
    clearInterval(pollTimerRef.current);
    resetGenRef.current += 1;
    const myGen = resetGenRef.current;

    ordersMapRef.current      = new Map();
    setOrders([]);
    pnoRef.current            = 1;
    setHasMore(true);
    setNewOrderCount(0);
    setNewOrderIds(new Set());
    latestPlacedAtRef.current = null;

    updateOrderCounts();

    fetchOrders(true).then(() => {
      if (resetGenRef.current !== myGen) return;
      if (isOnlineTabRef.current) {
        pollTimerRef.current = setInterval(() => pollFnRef.current?.(), POLL_INTERVAL);
      }
    });

    return () => clearInterval(pollTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeStatus, cafeid, status, date, orderID]);

  const handleSaved = () => setChangeStatus(p => !p);

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab !== 'neworder') setStatus(getStatusForTab(tab));
  };

  return (
    <div className="infinite-scroll-container">
      <style>{`
        .pending-alert-bar {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; padding: 9px 14px; margin: 0 0 10px;
          background: #fef3c7; border: 1px solid #f59e0b; border-radius: 8px;
          font-size: 13px; font-weight: 600; color: #92400e;
        }
        .pending-alert-bar--muted { background: #f3f4f6; border-color: #d1d5db; color: #6b7280; }
        .pab-text { display: flex; align-items: center; gap: 8px; }
        .pab-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #f59e0b; flex-shrink: 0;
          animation: pab-pulse 1.4s ease-in-out infinite;
        }
        .pending-alert-bar--muted .pab-dot { animation: none; background: #9ca3af; }
        @keyframes pab-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: .45; transform: scale(.8); }
        }
        .pab-mute-btn {
          border: 1px solid #f59e0b; background: white; color: #92400e;
          font-size: 12px; font-weight: 600; padding: 5px 12px; border-radius: 6px;
          cursor: pointer; white-space: nowrap;
        }
        .pending-alert-bar--muted .pab-mute-btn { border-color: #d1d5db; color: #6b7280; }
        .pab-mute-btn:hover { filter: brightness(0.97); }

        .sound-mute-toggle {
          display: inline-flex; align-items: center; gap: 5px;
          border: 1px solid #e5e7eb; background: white; color: #6b7280;
          font-size: 12px; font-weight: 600; padding: 6px 11px; border-radius: 7px;
          cursor: pointer; margin-left: auto;
        }
        .sound-mute-toggle.is-muted { color: #ef4444; border-color: #fecaca; background: #fef2f2; }
      `}</style>

      {newOrderCount > 0 && (
        <NewOrderToast count={newOrderCount} onDismiss={() => setNewOrderCount(0)} />
      )}

        {openStatusError && (
        <p className="edit-error" style={{ margin: '4px 0 8px' }}>{openStatusError}</p>
      )}
      {pendingAlertCount > 0 && (
        <PendingAlertBar
          count={pendingAlertCount}
          muted={soundMuted}
          onToggleMute={() => setSoundMuted(m => !m)}
        />
      )}

      {/* ── Tab row ── */}
      <div className="status-filter-row">
        <button className={activeTab === 'online' ? 'active-status' : ''}
          onClick={() => switchTab('online')}>
          Online <span className="count-badge">{onlineCount}</span>
        </button>
        <button className={activeTab === 'completed' ? 'active-status' : ''}
          onClick={() => switchTab('completed')}>
          Completed <span className="count-badge">{completedCount}</span>
        </button>
        <button className={activeTab === 'cancelled' ? 'active-status' : ''}
          onClick={() => switchTab('cancelled')}>
          Cancelled <span className="count-badge">{cancelledCount}</span>
        </button>

        <button className={activeTab === 'neworder' ? 'active-status' : ''}
          onClick={() => switchTab('neworder')}>
          + New Order
        </button>


<div className="status-filter-row-right"> 
        {/* Always-available mute toggle, independent of whether an alert is currently firing */}
        <button
          type="button"
          className={`sound-mute-toggle${soundMuted ? ' is-muted' : ''}`}
          onClick={() => setSoundMuted(m => !m)}
          title={soundMuted ? 'Unmute new-order alerts' : 'Mute new-order alerts'}
        >
          {soundMuted ? '🔕' : '🔔'} {soundMuted ? 'Muted' : 'Sound on'}
        </button>

        <a
        style={{'text-decoration':'none'}}
        href="tel:+911234567890"
       className="sound-mute-toggle"
  title="Call RestroPilots support"
>
  📞 +911234567890
</a>
        <OpenStatusToggle
          isOpen={isOpen}
          loading={openStatusLoading}
          onToggle={toggleOpenStatus}
        />


        
</div>



      </div>

      {/* ── Date / search filters ── */}
      {/* {activeTab !== 'neworder' && (
        <div className="order-filter-row">
          <input type="date" value={date || istToday()}
            onChange={e => { setDate(e.target.value); setIsDate(true); }} />
          <input type="text" value={orderID} onChange={e => setOrderId(e.target.value)}
            placeholder="Search by full Order ID" />
        </div>
      )} */}

      {/* ── Online tab: two-column layout (no InfiniteScroll, all loaded) ── */}
      {activeTab === 'neworder' ? (
        <TakeOrder cafeid={cafeid} socket={socket} />
      ) : isOnlineTab ? (
        <>
          {orders.length === 0 ? (
            <p className="scroll-end-msg">No active orders</p>
          ) : (
            <TwoColumnOnlinePanel
              orders={orders}
              cafeid={cafeid}
              socket={socket}
              changeStatus={changeStatus}
              setChangeStatus={setChangeStatus}
              isOnline={isOnlineTab}
              onOpenPayment={setPaymentOrder}
              onOpenEdit={setEditOrder}
              onOpenInvoice={setInvoiceOrderId}
              onOpenSendBill={setSendBillOrder}
              newOrderIds={newOrderIds}
              onStatusChanged={handleSaved}
            />
          )}
          {hasMore && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
              <button className="btn-load-more" onClick={() => fetchOrders(false)}>Load more</button>
            </div>
          )}
        </>
      ) : (
        /* ── Completed / Cancelled: single-column infinite scroll ── */
        <InfiniteScroll
          dataLength={orders.length}
          next={() => fetchOrders(false)}
          hasMore={hasMore}
          loader={
            <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
              <Spinner animation="border" size="sm" />
            </div>
          }
          endMessage={
            <p className="scroll-end-msg">
              {orders.length > 0 ? 'All orders loaded' : 'No orders found'}
            </p>
          }
        >
          {orders.map((order, idx) => {
            if (!order) return null;
            const id = safeStr(order.orderId, `tmp-${idx}`);
            return (
              <OrderCard
                key={id}
                order={order}
                cafeid={cafeid}
                socket={socket}
                changeStatus={changeStatus}
                setChangeStatus={setChangeStatus}
                isOnline={false}
                onOpenPayment={setPaymentOrder}
                onOpenEdit={setEditOrder}
                onOpenInvoice={setInvoiceOrderId}
                isNew={false}
                onOpenSendBill={setSendBillOrder}
                onStatusChanged={handleSaved}
                isPendingOnly={false}
              />
            );
          })}
        </InfiniteScroll>
      )}

      {/* ── Modals ── */}
      {paymentOrder && (
        <PaymentModal order={paymentOrder} onClose={() => setPaymentOrder(null)} onPaid={handleSaved} />
      )}
      {editOrder && (
        <EditOrderModal order={editOrder} cafeid={cafeid} onClose={() => setEditOrder(null)} onSaved={handleSaved} />
      )}
      {invoiceOrderId && (
        <InvoiceModal orderId={invoiceOrderId} onClose={() => setInvoiceOrderId(null)} />
      )}
      {sendBillOrder && (
        <SendBillModal order={sendBillOrder} onClose={() => setSendBillOrder(null)} />
      )}
    </div>
  );
};

export default Orders;